// src/screens/admin/adminCouponList/AdminCouponList.jsx

import React, { useState, useMemo, useEffect, useContext } from "react";
import axios from "axios";
import {
  Plus,
  Search,
  Eye,
  Pencil,
  Trash2,
  Tag,
  X,
} from "lucide-react";
import "../../../assets/css/admin/adminCouponList/AdminCouponList.css";
import AppContext from "../../../context/AppContext";

// ── Helpers ───────────────────────────────────────────────────────────────────

const statusClass = (s) =>
  s === "Active" ? "cpn-badge-active" : s === "Inactive" ? "cpn-badge-inactive" : "cpn-badge-expired";

const fmt = (v, type) =>
  type === "Percentage" ? `${v}%` : `Rs.${Number(v).toLocaleString()}`;

const EMPTY_FORM = {
  coupon_code: "", coupon_name: "", discount_type: "Percentage",
  discount_value: "", usage_limit: "", expiry_date: "", status: "Active",
};

// ── Main Component ────────────────────────────────────────────────────────────

const AdminCouponList = () => {
  const { baseUrl, authToken } = useContext(AppContext);

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // ── API: View Coupons ────────────────────────────────────────────────────────

  const fetchCoupons = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/coupon/viewCoupons`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.coupons) {
          setCoupons(response.data.coupons);
        }
      })
      .catch((error) => {
        console.error("Error fetching coupons:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // ── API: Create Coupon ───────────────────────────────────────────────────────

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("coupon_code", form.coupon_code);
    formData.append("coupon_name", form.coupon_name);
    formData.append("discount_type", form.discount_type);
    formData.append("discount_value", form.discount_value);
    formData.append("usage_limit", form.usage_limit);
    formData.append("expiry_date", form.expiry_date);
    formData.append("status", form.status);

    axios
      .post(`${baseUrl}/admin/coupon/createCoupon`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success) {
          fetchCoupons();
          closeModal();
        } else {
          setErrors({ submit: response.data.message || "Coupon not created. Try again later." });
        }
      })
      .catch((error) => {
        console.error("Error creating coupon:", error);
        setErrors({ submit: "Something went wrong. Please try again." });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // ── API: Update Coupon ───────────────────────────────────────────────────────

  const handleUpdate = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("id", selected.id);
    formData.append("coupon_code", form.coupon_code);
    formData.append("coupon_name", form.coupon_name);
    formData.append("discount_type", form.discount_type);
    formData.append("discount_value", form.discount_value);
    formData.append("usage_limit", form.usage_limit);
    formData.append("expiry_date", form.expiry_date);
    formData.append("status", form.status);

    axios
      .post(`${baseUrl}/admin/coupon/updateCoupon`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success) {
          fetchCoupons();
          closeModal();
        } else {
          setErrors({ submit: response.data.message || "Coupon not updated. Try again later." });
        }
      })
      .catch((error) => {
        console.error("Error updating coupon:", error);
        setErrors({ submit: "Something went wrong. Please try again." });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // ── API: Delete Coupon ───────────────────────────────────────────────────────

  const handleDelete = () => {
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("id", selected.id);

    axios
      .post(`${baseUrl}/admin/coupon/deleteCoupon`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success) {
          fetchCoupons();
          closeModal();
        } else {
          setErrors({ submit: response.data.message || "Coupon not deleted. Try again later." });
        }
      })
      .catch((error) => {
        console.error("Error deleting coupon:", error);
        setErrors({ submit: "Something went wrong. Please try again." });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // ── Filter ───────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return coupons.filter(
      (c) =>
        c.coupon_code?.toLowerCase().includes(q) ||
        c.coupon_name?.toLowerCase().includes(q)
    );
  }, [coupons, search]);

  const handleSearch = (e) => { setSearch(e.target.value); };

  // ── Modal helpers ────────────────────────────────────────────────────────────

  const openAdd = () => { setForm(EMPTY_FORM); setErrors({}); setModal("add"); };
  const openEdit = (c) => {
    setSelected(c);
    setForm({
      coupon_code: c.coupon_code,
      coupon_name: c.coupon_name,
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      usage_limit: c.usage_limit,
      expiry_date: c.expiry_date,
      status: c.status,
    });
    setErrors({});
    setModal("edit");
  };
  const openDelete = (c) => { setSelected(c); setErrors({}); setModal("delete"); };
  const openView = (c) => { setSelected(c); setModal("view"); };
  const closeModal = () => { setModal(null); setSelected(null); setErrors({}); };

  // ── Form ─────────────────────────────────────────────────────────────────────

  const handleForm = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.coupon_code.trim()) e.coupon_code = "Coupon code is required.";
    if (!form.coupon_name.trim()) e.coupon_name = "Coupon name is required.";
    if (!form.discount_value) e.discount_value = "Discount value is required.";

    if (form.expiry_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(form.expiry_date);

      if (selectedDate < today) {
        e.expiry_date = "Expiry date cannot be in the past.";
      }
    }

    return e;
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="cpn-page">

      {/* ── PAGE HEADER ── */}
      <div className="cpn-page-header">
        <div>
          <h1 className="cpn-page-title">Coupon Codes</h1>
          <p className="cpn-page-subtitle">Manage discount coupons for your store.</p>
        </div>
      </div>

      {/* ── ACTION BAR ── */}
      <div className="cpn-action-bar">
        <div className="cpn-search-wrap">
          <Search size={16} className="cpn-search-icon" />
          <input
            className="cpn-search"
            placeholder="Search coupon code or coupon name..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <button className="cpn-btn-primary" onClick={openAdd}>
          <Plus size={16} strokeWidth={2.5} />
          Add Coupon
        </button>
      </div>

      {/* ── TABLE CARD ── */}
      <div className="cpn-card">
        {loading ? (
          <div className="cpn-empty">
            <p>Loading...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="cpn-empty">
            <div className="cpn-empty-icon"><Tag size={40} strokeWidth={1.3} /></div>
            <h3>No Coupons Found</h3>
            <p>Create your first coupon to offer discounts.</p>
            <button className="cpn-btn-primary" onClick={openAdd}>
              <Plus size={15} strokeWidth={2.5} /> Add Coupon
            </button>
          </div>
        ) : (
          <div className="cpn-table-wrap">
            <table className="cpn-table">
              <thead>
                <tr>
                  <th>Coupon Code</th>
                  <th>Coupon Name</th>
                  <th>Discount</th>
                  <th>Available</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td><span className="cpn-code-pill">{c.coupon_code}</span></td>
                    <td className="cpn-name">{c.coupon_name}</td>
                    <td className="cpn-value">{fmt(c.discount_value, c.discount_type)}</td>
                    <td>{c.usage_limit - c.used}</td>
                    <td>{c.expiry_date}</td>
                    <td><span className={`cpn-badge ${statusClass(c.status)}`}>{c.status}</span></td>
                    <td>
                      <div className="cpn-actions">
                        <button className="cpn-act-btn cpn-act-view" onClick={() => openView(c)} title="View"><Eye size={15} /></button>
                        <button className="cpn-act-btn cpn-act-edit" onClick={() => openEdit(c)} title="Edit"><Pencil size={15} /></button>
                        <button className="cpn-act-btn cpn-act-del" onClick={() => openDelete(c)} title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      {(modal === "add" || modal === "edit") && (
        <div className="cpn-overlay" onClick={closeModal}>
          <div className="cpn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cpn-modal-header">
              <h2>{modal === "add" ? "Add Coupon" : "Edit Coupon"}</h2>
              <button className="cpn-modal-close" onClick={closeModal} disabled={isSubmitting}><X size={18} /></button>
            </div>
            <div className="cpn-modal-body">
              <div className="cpn-form-grid">

                <div className="cpn-field">
                  <label>Coupon Code <span className="cpn-req">*</span></label>
                  <input name="coupon_code" value={form.coupon_code} onChange={handleForm} placeholder="e.g. SAVE20" className={errors.coupon_code ? "cpn-input-err" : ""} />
                  {errors.coupon_code && <p className="cpn-err-msg">{errors.coupon_code}</p>}
                </div>

                <div className="cpn-field">
                  <label>Coupon Name <span className="cpn-req">*</span></label>
                  <input name="coupon_name" value={form.coupon_name} onChange={handleForm} placeholder="e.g. Summer Sale" className={errors.coupon_name ? "cpn-input-err" : ""} />
                  {errors.coupon_name && <p className="cpn-err-msg">{errors.coupon_name}</p>}
                </div>

                <div className="cpn-field">
                  <label>Discount Type</label>
                  <select name="discount_type" value={form.discount_type} onChange={handleForm}>
                    <option value="Percentage">Percentage</option>
                    <option value="Flat">Flat Amount</option>
                  </select>
                </div>

                <div className="cpn-field">
                  <label>Discount Value <span className="cpn-req">*</span></label>
                  <input name="discount_value" type="number" value={form.discount_value} onChange={handleForm} placeholder={form.discount_type === "Percentage" ? "e.g. 20" : "e.g. 500"} className={errors.discount_value ? "cpn-input-err" : ""} />
                  {errors.discount_value && <p className="cpn-err-msg">{errors.discount_value}</p>}
                </div>

                <div className="cpn-field">
                  <label>Usage Limit</label>
                  <input name="usage_limit" type="number" value={form.usage_limit} onChange={handleForm} placeholder="e.g. 100" />
                </div>

                <div className="cpn-field">
                  <label>Expiry Date</label>
                  <input
                    name="expiry_date"
                    type="date"
                    value={form.expiry_date}
                    onChange={handleForm}
                    min={new Date().toISOString().split("T")[0]}
                    className={errors.expiry_date ? "cpn-input-err" : ""}
                  />
                  {errors.expiry_date && <p className="cpn-err-msg">{errors.expiry_date}</p>}
                </div>

                <div className="cpn-field">
                  <label>Status</label>
                  <select name="status" value={form.status} onChange={handleForm}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

              </div>

              {errors.submit && (
                <p className="cpn-err-msg" style={{ textAlign: "center", marginTop: "12px" }}>
                  {errors.submit}
                </p>
              )}

            </div>
            <div className="cpn-modal-footer">
              <button className="cpn-btn-ghost" onClick={closeModal} disabled={isSubmitting}>Cancel</button>
              <button className="cpn-btn-primary" onClick={modal === "add" ? handleSave : handleUpdate} disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : modal === "add" ? "Save Coupon" : "Update Coupon"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE MODAL ── */}
      {modal === "delete" && (
        <div className="cpn-overlay" onClick={closeModal}>
          <div className="cpn-modal cpn-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="cpn-modal-header">
              <h2>Delete Coupon</h2>
              <button className="cpn-modal-close" onClick={closeModal} disabled={isSubmitting}><X size={18} /></button>
            </div>
            <div className="cpn-modal-body cpn-delete-body">
              <div className="cpn-delete-icon"><Trash2 size={28} /></div>
              <p className="cpn-delete-title">Are you sure you want to delete this coupon?</p>
              <p className="cpn-delete-sub">This action cannot be undone.</p>
              <div className="cpn-delete-meta">
                <span className="cpn-code-pill">{selected?.coupon_code}</span>
                <span className="cpn-name">{selected?.coupon_name}</span>
              </div>
              {errors.submit && (
                <p className="cpn-err-msg" style={{ textAlign: "center", marginTop: "8px" }}>
                  {errors.submit}
                </p>
              )}
            </div>
            <div className="cpn-modal-footer">
              <button className="cpn-btn-ghost" onClick={closeModal} disabled={isSubmitting}>Cancel</button>
              <button className="cpn-btn-danger" onClick={handleDelete} disabled={isSubmitting}>
                {isSubmitting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW MODAL ── */}
      {modal === "view" && selected && (
        <div className="cpn-overlay" onClick={closeModal}>
          <div className="cpn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cpn-modal-header">
              <h2>Coupon Details</h2>
              <button className="cpn-modal-close" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="cpn-modal-body">
              <div className="cpn-view-top">
                <span className="cpn-code-pill cpn-code-lg">{selected.coupon_code}</span>
                <span className={`cpn-badge ${statusClass(selected.status)}`}>{selected.status}</span>
              </div>
              <div className="cpn-view-grid">
                <div className="cpn-view-item"><span className="cpn-vl">Coupon Name</span><span className="cpn-vv">{selected.coupon_name}</span></div>
                <div className="cpn-view-item"><span className="cpn-vl">Discount Type</span><span className="cpn-vv">{selected.discount_type}</span></div>
                <div className="cpn-view-item"><span className="cpn-vl">Discount Value</span><span className="cpn-vv cpn-vv-accent">{fmt(selected.discount_value, selected.discount_type)}</span></div>
                <div className="cpn-view-item"><span className="cpn-vl">Usage Limit</span><span className="cpn-vv">{selected.usage_limit}</span></div>
                <div className="cpn-view-item"><span className="cpn-vl">Available</span><span className="cpn-vv">{selected.usage_limit - selected.used}</span></div>
                <div className="cpn-view-item"><span className="cpn-vl">Expiry Date</span><span className="cpn-vv">{selected.expiry_date || "—"}</span></div>
              </div>
            </div>
            <div className="cpn-modal-footer">
              <button className="cpn-btn-ghost" onClick={closeModal}>Close</button>
              <button className="cpn-btn-primary" onClick={() => { closeModal(); openEdit(selected); }}>
                <Pencil size={14} /> Edit Coupon
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminCouponList;